import React, { FC, lazy, Suspense, useEffect, useMemo } from 'react';

import './DashboardPage.scss';

import { SideBar } from './SideBar';
import { DataView } from '../DataView';
import {
  WearableErrorSnackBar,
  TaskErrorSnackBar,
  CalendarEventSnackBar,
  ResizeWrapper,
  CoreActions,
} from '../../components';
import { WalkthroughController } from '../../components/walkthrough';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  calendarViewSwitcherAtom,
  homeViewSwitcherAtom,
  viewSwitcherAtom,
} from '../../data-access';
import { FirstOpenDialogGroup } from './FirstOpenDialogGroup';
import { CircularProgress } from '@mui/material';
import { useLocalStorage } from '@uidotdev/usehooks';

import { DataHeader } from '../../components/DataView/ReDataView/DataHeader';
import { AISuggestedEventSheetWrapper } from './CalendarView';

const CalendarWeekMonthView = lazy(() =>
  import('./CalendarWeekMonthView').then((module) => ({ default: module.CalendarWeekMonthView }))
);
const KanbanView = lazy(() =>
  import('./KanbanView').then((module) => ({ default: module.KanbanView }))
);
const OneDayCalendarView = lazy(() =>
  import('./CalendarView').then((module) => ({ default: module.OneDayCalendarView }))
);

const DashboardPage: FC = () => {
  const [currentView, setCurrentView] = useRecoilState(viewSwitcherAtom);
  const [currentHomeView, setCurrentHomeView] = useRecoilState(homeViewSwitcherAtom);
  const currentCalendarView = useRecoilValue(calendarViewSwitcherAtom);

  const showKanbanWithOneDayCalendar = useMemo(
    () => currentCalendarView === 'day',
    [currentCalendarView]
  );

  const showCoreActions = useMemo(() => {
    if (currentView === 'Data') {
      return false;
    }

    return currentCalendarView === 'day';
  }, [currentCalendarView, currentView]);

  const [calendarWidth, setCalendarWidth] = useLocalStorage('onedaycalendar_width', 400);

  useEffect(() => {
    if (currentView !== 'Home') {
      setCurrentView('Home');
    }

    const expectedHomeView = currentCalendarView === 'day' ? 'Todo' : 'Calendar';
    if (currentHomeView !== expectedHomeView) {
      setCurrentHomeView(expectedHomeView);
    }
  }, [
    currentCalendarView,
    currentHomeView,
    currentView,
    setCurrentHomeView,
    setCurrentView,
  ]);

  return (
    <>
      <div className="dashboard-page dashboard-page--new-tabs">
        <SideBar className="dashboard-page__side-bar" />
        <div className="dashboard-page__content">
          <div
            className={`dashboard-page__data-actions${
              !showCoreActions ? ' dashboard-page__core-actions--data' : ''
            }`}
          >
            {currentView === 'Data' ? <DataHeader /> : null}
          </div>
          {currentView === 'Home' ? (
            <div className="dashboard-page__kanban-calendar-container">
              {showKanbanWithOneDayCalendar ? (
                <Suspense
                  fallback={
                    <div className="dashboard-page__loader">
                      <CircularProgress />
                    </div>
                  }
                >
                  <KanbanView className="dashboard-page__kanban-calendar-container__kanban-view" />
                  <ResizeWrapper
                    minWidth={300}
                    maxWidth={800}
                    initialWidth={calendarWidth}
                    onWidthChange={setCalendarWidth}
                  >
                    <OneDayCalendarView calendarWidth={calendarWidth} />
                  </ResizeWrapper>
                </Suspense>
              ) : (
                <Suspense
                  fallback={
                    <div className="dashboard-page__loader">
                      <CircularProgress />
                    </div>
                  }
                >
                  <CalendarWeekMonthView className="dashboard-page__calendar-week-month-view" />
                </Suspense>
              )}
            </div>
          ) : (
            <div className="dashboard-page__data-view-container">
              <DataView />
            </div>
          )}
        </div>
      </div>
      <WearableErrorSnackBar />
      <TaskErrorSnackBar />
      <CalendarEventSnackBar />
      <FirstOpenDialogGroup />
      <AISuggestedEventSheetWrapper />
      <WalkthroughController />
    </>
  );
};

export default DashboardPage;
